// Aba "Tendências & Causa-Raiz" — análises da operação como um todo (não de um
// time específico): evolução temporal da reincidência + fluxo de causa-raiz.
// Conteúdo SIMULADO (exige dado em nível de chamado) — sinalizado com chip.
import { alpha } from "@mui/material/styles";
import { Box, Grid, Paper, Stack, Typography } from "@mui/material";
import {
  PriorityHighOutlined,
  ReplayCircleFilledOutlined,
  ScienceOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { ChartCard, MetricCard, formatInt } from "./shared";
import { GUIDES, MetricInfo } from "./metricInfo";
import {
  CartaControle,
  CurvaReincidencia,
  Heatmap,
  SankeyMotivos,
  SimChip,
} from "./simCharts";
import { cronicos, reincidenciaPorDia, spcSemanal } from "./simulatedData";

export function TendenciasCausaRaizTab({ styles, theme }) {
  const cronicosCriticos = cronicos.distribuicao
    .filter((d) => ["3x", "4x", "5x+"].includes(d.faixa))
    .reduce((s, d) => s + d.clientes, 0);

  return (
    <Grid
      container
      spacing={1}
      columns={{ xs: 2, sm: 4, md: 6, lg: 10, xl: 10 }}
      sx={{ maxWidth: "100%", minWidth: 0 }}
    >
      {/* Banner */}
      <Grid item xs={2} sm={4} md={6} lg={10}>
        <Paper
          sx={{
            ...styles.filterPaper,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            borderColor: alpha(theme.palette.warning.main, 0.4),
            backgroundColor: alpha(theme.palette.warning.main, 0.06),
          }}
          elevation={0}
        >
          <ScienceOutlined sx={{ color: theme.palette.warning.main }} />
          <Box>
            <Typography sx={styles.cardSubtitle}>
              Visões que cruzam toda a operação (não um time). Exigem dado em nível de
              chamado (datas, par abertura↔fechamento) que não está no relatório —
              por isso estão <b>simuladas</b>, demonstrando o potencial.
            </Typography>
          </Box>
        </Paper>
      </Grid>

      {/* KPIs */}
      <Grid item xs={2} sm={2} md={3} lg={4}>
        <MetricCard
          styles={styles}
          theme={theme}
          icon={ReplayCircleFilledOutlined}
          accent={theme.palette.warning.main}
          title="Reincidência na 1ª semana"
          value={`${reincidenciaPorDia[6]?.acumulado}%`}
          helper="Acumulado até o 7º dia (de 25,1% em 90d)"
        />
      </Grid>
      <Grid item xs={2} sm={2} md={3} lg={3}>
        <MetricCard
          styles={styles}
          theme={theme}
          icon={WarningAmberOutlined}
          accent={theme.palette.error.main}
          title="Semanas fora de controle"
          value={formatInt(spcSemanal.filter((s) => s.foraControle).length)}
          helper="Acima do limite estatístico (UCL)"
        />
      </Grid>
      <Grid item xs={2} sm={2} md={3} lg={3}>
        <MetricCard
          styles={styles}
          theme={theme}
          icon={PriorityHighOutlined}
          accent={theme.palette.info.main}
          title="Instalações crônicas (3x+)"
          value={formatInt(cronicosCriticos)}
          helper="Provável causa de infraestrutura"
        />
      </Grid>

      {/* Tendências */}
      <Grid item xs={2} sm={4} md={6} lg={6}>
        <ChartCard
          styles={styles}
          theme={theme}
          title="Curva de reincidência por dia"
          subtitle="Quando o problema volta após o fechamento"
          topRight={
            <Stack direction="row" gap={1} alignItems="center">
              <MetricInfo theme={theme} guide={GUIDES.curva} />
              <SimChip theme={theme} />
            </Stack>
          }
        >
          <CurvaReincidencia styles={styles} theme={theme} />
        </ChartCard>
      </Grid>
      <Grid item xs={2} sm={4} md={6} lg={4}>
        <ChartCard
          styles={styles}
          theme={theme}
          title="Carta de controle (SPC)"
          subtitle="Reincidência semanal vs limites estatísticos"
          topRight={
            <Stack direction="row" gap={1} alignItems="center">
              <MetricInfo theme={theme} guide={GUIDES.spc} />
              <SimChip theme={theme} />
            </Stack>
          }
        >
          <CartaControle styles={styles} theme={theme} />
        </ChartCard>
      </Grid>

      {/* Causa-raiz */}
      <Grid item xs={2} sm={4} md={6} lg={6}>
        <ChartCard
          styles={styles}
          theme={theme}
          title="Abertura → Fechamento"
          subtitle="Fluxo de causa-raiz dos chamados"
          topRight={
            <Stack direction="row" gap={1} alignItems="center">
              <MetricInfo theme={theme} guide={GUIDES.sankey} />
              <SimChip theme={theme} />
            </Stack>
          }
        >
          <SankeyMotivos styles={styles} theme={theme} />
        </ChartCard>
      </Grid>
      <Grid item xs={2} sm={4} md={6} lg={4}>
        <ChartCard
          styles={styles}
          theme={theme}
          title="Cidade × Motivo"
          subtitle="Concentração de chamados por motivo"
          topRight={<SimChip theme={theme} />}
        >
          <Heatmap theme={theme} />
        </ChartCard>
      </Grid>
    </Grid>
  );
}
